import { InteractionType } from '@azure/msal-browser';
import { MsalAuthenticationTemplate } from '@azure/msal-react';
import { Outlet } from 'react-router';
import { MainSectionTemplate } from '../../components/AdminTemplates';
import LosingChangesConfimationDialog from '../../components/LosingChangesConfimationDialog';
import ProtectedRoute from '../../components/ProtectedRoute';
import AppDataProvider from '../../components/Providers/AppDataProvider';
import CurrentUserProvider from '../../components/Providers/CurrentUserProvider';
import { renderSkeletonCards } from '../../components/SkeletonElements';
import { useLanguage } from '../../contexts/language';
import { useAdminSectionActivityHandler } from '../../hooks/useAdminSectionActivityHandler';
import useNavigationGuard from '../../hooks/useNavigationGuard';
import AdminHeader from './AdminHeader';
import { useCallback, useState } from 'react';
import CancelChangesRequesterContext, {
  CancelChangesRequester,
} from '../../contexts/cancelChangesRequester';
import { Language } from '../../types/language';

const MainLayout = () => {
  const [cancelChangesPendingCallback, setCancelChangesPendingCallback] =
    useState<null | (() => void)>(null);
  const requestCancelChanges = useCallback<CancelChangesRequester>(callback => {
    setCancelChangesPendingCallback(() => callback);
  }, []);

  const { cancelLeave, confirmLeave, isBlocked } = useNavigationGuard();
  return (
    <CancelChangesRequesterContext.Provider value={requestCancelChanges}>
      <div className="main-container bg-iots-lightgrey">
        <AdminHeader />
        <main>
          <Outlet />
        </main>
        <footer className="bg-iots-gold-500 p-6"></footer>
        <LosingChangesConfimationDialog
          isOpen={isBlocked || Boolean(cancelChangesPendingCallback)}
          onClose={() => {
            if (isBlocked) {
              cancelLeave();
            }
            if (cancelChangesPendingCallback) {
              setCancelChangesPendingCallback(null);
            }
          }}
          onConfirm={() => {
            if (isBlocked) {
              confirmLeave();
            }
            if (cancelChangesPendingCallback) {
              setCancelChangesPendingCallback(null);
              cancelChangesPendingCallback();
            }
          }}
        />
      </div>
    </CancelChangesRequesterContext.Provider>
  );
};

const AdminDataWrapper = ({ children }: { children: React.ReactNode }) => {
  useAdminSectionActivityHandler();

  const loadingContent = (
    <MainSectionTemplate title={''}>
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {renderSkeletonCards(10)}
      </ul>
    </MainSectionTemplate>
  );

  return (
    <CurrentUserProvider loadingContent={loadingContent}>
      <AppDataProvider loadingContent={loadingContent}>
        {children}
      </AppDataProvider>
    </CurrentUserProvider>
  );
};

const AdminWrapperMainLayout = () => {
  const { language } = useLanguage();
  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={{
        extraQueryParameters: {
          ui_locales: language === Language.EN ? 'en' : 'ga-IE',
        },
      }}
    >
      <ProtectedRoute>
        <AdminDataWrapper>
          <MainLayout />
        </AdminDataWrapper>
      </ProtectedRoute>
    </MsalAuthenticationTemplate>
  );
};

export default AdminWrapperMainLayout;
